import React, { useEffect, useState, useCallback, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Grid,
	Select,
	FormHelperText,
	MenuItem,
	FormControl,
	InputLabel,
	Switch,
	FormControlLabel,
	Box,
	CircularProgress,
	Alert,
	Typography,
	Slider,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { resolveEmbeddableImage } from "../../../providers/PDFGeneratorProvider/resolveEmbeddableImage";
import {
	SettingManagement,
	CreateSettingPayload,
	UpdateSettingPayload,
} from "../../../domain/settings.interface";
// SIMS-8458 AC7/AC8 — import the documented defaults rather than repeating the literals.
// The existing tertiaryColor / surfaceTint fields duplicate theirs ("#7034E7", "#DFF5FF")
// in both the useState seed and the buildJson comparison, so a change to a default in
// colorUtils.ts would silently disagree with this screen. Importing keeps one source of
// truth, which is also what AC6 asks for.
import {
	DEFAULT_SUCCESS_LIGHT,
	DEFAULT_SUCCESS_DARK,
	DEFAULT_BRAND_FAINT,
	DEFAULT_DIVIDER,
	DEFAULT_SURFACE_TWO,
	DEFAULT_TEXT_PRIMARY,
	// No DEFAULT_TEXT_SECONDARY here: the screen already exposes Text Secondary (with
	// opacity, via textSecondaryMain), and AC4 repoints that existing role rather than
	// adding a second control. Its default lives in colorUtils.ts where the palette reads it.
} from "../../../utils/colorUtils";
// SIMS-8368 (FR-12) — the certificate picker's defaults come from the shared contract.
// SIMS-8351 (FR-8) adds the title-font list and its "match body" sentinel.
import {
	CERTIFICATE_CONFIG_DEFAULTS,
	CERTIFICATE_TITLE_FONTS,
	CERTIFICATE_TITLE_FONT_MATCH_BODY,
} from "../../../domain/certificateConfig";

interface SettingsModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (data: CreateSettingPayload | UpdateSettingPayload) => Promise<void>;
	editingSetting?: SettingManagement | null;
	isAccountView?: boolean;
	selectedAccountName?: string;
}

interface FormData {
	product: string;
	application: "mobile" | "backoffice" | "platform";
	module: string;
	section: string;
	name: string;
	description: string;
	type: "string" | "number" | "boolean" | "object" | "array";
	default: string;
	active: boolean;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
	open,
	onClose,
	onSave,
	editingSetting,
	isAccountView = false,
	selectedAccountName,
}) => {
	const [isLoading, setIsLoading] = React.useState(false);

	// Determine if we're editing an account override
	const isEditingAccountOverride =
		isAccountView && editingSetting?.accountOverride;

	// Helper function to safely display default value
	const formatDefaultValue = (value: string): string => {
		if (typeof value === "object") {
			return JSON.stringify(value);
		}
		return String(value);
	};

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<FormData>({
		defaultValues: {
			product: process.env.APP_APPLICATION_ID || "sims",
			application: "backoffice",
			module: "",
			section: "",
			name: "",
			description: "",
			type: "string",
			default: "",
			active: true,
		},
	});

	const typeValue = watch("type");

	// Populate form when editing
	useEffect(() => {
		if (editingSetting && open) {
			// If editing an account override, use the override value
			const valueToEdit = isEditingAccountOverride
				? editingSetting.accountOverride?.value
				: editingSetting.default;

			reset({
				product: editingSetting.product,
				application: editingSetting.application,
				module: editingSetting.module,
				section: editingSetting.section,
				name: editingSetting.name,
				description: editingSetting.description,
				type: editingSetting.type,
				default:
					typeof valueToEdit === "object"
						? JSON.stringify(valueToEdit)
						: String(valueToEdit),
				active: isEditingAccountOverride
					? (editingSetting.accountOverride?.active ?? true)
					: editingSetting.active,
			});
		} else if (!open) {
			reset({
				product: process.env.APP_APPLICATION_ID || "sims",
				application: "backoffice",
				module: "",
				section: "",
				name: "",
				description: "",
				type: "string",
				default: "",
				active: true,
			});
		}
	}, [editingSetting, open, reset, isEditingAccountOverride]);

	const onSubmit = async (data: FormData) => {
		setIsLoading(true);
		try {
			// Send default value as string - backend will handle conversions
			const payload = {
				product: data.product,
				application: data.application,
				module: data.module,
				section: data.section,
				name: data.name,
				description: data.description,
				type: data.type,
				default: data.default, // Always send as string
				active: data.active,
			};

			await onSave(payload);
		} catch (error) {
			// Error handled by React Query
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				{editingSetting
					? isEditingAccountOverride
						? `Edit Account Override - ${selectedAccountName}`
						: `Edit Setting${isAccountView ? ` - Create Override for ${selectedAccountName}` : ""}`
					: isAccountView
						? `Create Account Override - ${selectedAccountName}`
						: "Create New Setting"}
			</DialogTitle>
			<DialogContent>
				{isLoading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						minHeight={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box component="form" sx={{ mt: 2 }}>
						{isAccountView && (
							<Alert severity="info" sx={{ mb: 2 }}>
								{isEditingAccountOverride
									? `You are editing the account-specific override for ${selectedAccountName}. This will only affect this account.`
									: `You are creating an account-specific override for ${selectedAccountName}. This will override the global default value for this account only.`}
							</Alert>
						)}

						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<Controller
									name="application"
									control={control}
									rules={{ required: "Application is required" }}
									render={({ field }) => (
										<FormControl
											fullWidth
											error={!!errors.application}
											disabled={isAccountView}
										>
											<InputLabel>Application</InputLabel>
											<Select {...field} label="Application">
												<MenuItem value="mobile">Mobile</MenuItem>
												<MenuItem value="backoffice">Backoffice</MenuItem>
												<MenuItem value="platform">Platform</MenuItem>
											</Select>
										</FormControl>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="module"
									control={control}
									rules={{ required: "Module is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Module"
											fullWidth
											error={!!errors.module}
											helperText={errors.module?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="section"
									control={control}
									rules={{ required: "Section is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Section"
											fullWidth
											error={!!errors.section}
											helperText={errors.section?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="name"
									control={control}
									rules={{ required: "Name is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Name"
											fullWidth
											error={!!errors.name}
											helperText={errors.name?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12}>
								<Controller
									name="description"
									control={control}
									rules={{ required: "Description is required" }}
									render={({ field }) => (
										<TextField
											{...field}
											label="Description"
											fullWidth
											multiline
											rows={2}
											error={!!errors.description}
											helperText={errors.description?.message}
											disabled={isAccountView}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="type"
									control={control}
									rules={{ required: "Type is required" }}
									render={({ field }) => (
										<FormControl
											fullWidth
											error={!!errors.type}
											disabled={isAccountView}
										>
											<InputLabel>Type</InputLabel>
											<Select {...field} label="Type">
												<MenuItem value="string">String</MenuItem>
												<MenuItem value="number">Number</MenuItem>
												<MenuItem value="boolean">Boolean</MenuItem>
												<MenuItem value="object">Object</MenuItem>
												<MenuItem value="array">Array</MenuItem>
											</Select>
										</FormControl>
									)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Controller
									name="active"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={<Switch {...field} checked={field.value} />}
											label="Active"
										/>
									)}
								/>
							</Grid>

							<Grid item xs={12}>
								{/* Custom editors for specific settings, raw JSON for others */}
								{(() => {
									const settingName = editingSetting?.name || watch("name");
									if (settingName === "themeColors") {
										return <ThemeColorPicker control={control} watch={watch} />;
									}
									if (settingName === "certificateConfig") {
										return (
											<CertificateConfigPicker
												control={control}
												watch={watch}
												error={errors.default?.message}
											/>
										);
									}
									if (settingName === "beneficiaryTerms") {
										return (
											<BeneficiaryTermsPicker control={control} watch={watch} />
										);
									}
									return (
										<Controller
											name="default"
											control={control}
											rules={{ required: "Default value is required" }}
											render={({ field }) => (
												<TextField
													{...field}
													label={
														isAccountView
															? "Override Value (Account-Specific)"
															: "Default Value"
													}
													fullWidth
													multiline={
														typeValue === "object" || typeValue === "array"
													}
													rows={
														typeValue === "object" || typeValue === "array"
															? 4
															: 1
													}
													error={!!errors.default}
													helperText={
														errors.default?.message ||
														(typeValue === "object" || typeValue === "array"
															? "Enter valid JSON"
															: isAccountView && editingSetting
																? `Global default: ${formatDefaultValue(editingSetting.default)}`
																: "")
													}
												/>
											)}
										/>
									);
								})()}
							</Grid>
						</Grid>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isLoading}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit(onSubmit)}
					variant="contained"
					color="primary"
					disabled={isLoading}
				>
					{editingSetting ? "Update" : "Create"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

/**
 * Inline color swatch with native color picker + hex text field.
 */
// Parses a hex or rgba(r,g,b,a) string into { hex, opacityPct }.
function parseColorValue(value: string): { hex: string; opacityPct: number } {
	const rgba = value.match(
		/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\s*\)/,
	);
	if (rgba) {
		const hex = `#${[rgba[1], rgba[2], rgba[3]]
			.map((n) => parseInt(n).toString(16).padStart(2, "0"))
			.join("")}`;
		return { hex, opacityPct: Math.round(parseFloat(rgba[4] ?? "1") * 100) };
	}
	if (/^#[0-9A-Fa-f]{6}$/.test(value)) return { hex: value, opacityPct: 100 };
	return { hex: "#FFFFFF", opacityPct: 100 };
}

// Converts hex + opacity% to a CSS color string.
// Returns hex when opacity is 100%, rgba(...) otherwise.
function buildColorValue(hex: string, opacityPct: number): string {
	if (opacityPct >= 100) return hex;
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${parseFloat((opacityPct / 100).toFixed(2))})`;
}

// Color picker with an opacity slider. Emits hex (100%) or rgba(<100%).
function ColorFieldWithOpacity({
	label,
	hexValue,
	opacityPct,
	placeholder,
	caption,
	onHexChange,
	onOpacityChange,
}: {
	label: string;
	hexValue: string;
	opacityPct: number;
	placeholder: string;
	caption: string;
	onHexChange: (hex: string) => void;
	onOpacityChange: (opacity: number) => void;
}) {
	const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(hexValue);
	return (
		<Box>
			<Box display="flex" alignItems="center" gap={1}>
				<input
					type="color"
					value={isValidHex ? hexValue : "#FFFFFF"}
					onChange={(e) => onHexChange(e.target.value)}
					style={{
						width: 40,
						height: 40,
						border: "1px solid #ccc",
						borderRadius: 8,
						cursor: "pointer",
						padding: 2,
						flexShrink: 0,
					}}
				/>
				<TextField
					label={label}
					value={hexValue}
					onChange={(e) => onHexChange(e.target.value)}
					size="small"
					sx={{ flex: 1 }}
					placeholder={placeholder}
				/>
				<TextField
					label="Opacity %"
					type="number"
					value={opacityPct}
					onChange={(e) => {
						const val = Math.min(
							100,
							Math.max(0, parseInt(e.target.value) || 0),
						);
						onOpacityChange(val);
					}}
					inputProps={{ min: 0, max: 100, step: 1 }}
					size="small"
					sx={{ width: 90 }}
				/>
			</Box>
			<Box sx={{ px: 0.5, mt: 0.5 }}>
				<Slider
					value={opacityPct}
					min={0}
					max={100}
					step={1}
					onChange={(_e, val) => onOpacityChange(val as number)}
					size="small"
				/>
			</Box>
			{/* `display: block` + a small negative top margin: Typography renders inline by
			    default, so the caption sat in the flow with no box of its own, while the
			    Slider above contributes its own bottom spacing. The gap above the hint ended
			    up larger than the gap below it, which read as though the hint belonged to the
			    NEXT field rather than this one. Pulling it up and giving it a bottom margin
			    puts the caption visibly inside its own control. */}
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", mt: -0.5, mb: 1, lineHeight: 1.4 }}
			>
				{caption}
				{opacityPct < 100 && isValidHex
					? ` — ${buildColorValue(hexValue, opacityPct)}`
					: ""}
			</Typography>
		</Box>
	);
}

/**
 * SIMS-8351 — a certificate settings section heading, per the Hi-Fi hand-off:
 * `font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase;
 *  color:var(--text-secondary); border-bottom:1px solid var(--divider); padding-bottom:8px`.
 *
 * Deliberately a LOCAL component used by the five CERTIFICATE sections only. `variant="subtitle2"`
 * appears seven times in this file, but "Theme Colors & Font" and "Beneficiary Terminology" belong to
 * other tabs the hand-off never covers — restyling all seven would change surfaces no certificate AC
 * owns. (Screen 01's subsection headings are a different treatment again: 15px/700 in the brand
 * colour, not uppercased. Don't cross-copy.)
 */
function CertificateSectionHeading({
	children,
}: {
	children: React.ReactNode;
}) {
	return (
		<Typography
			sx={{
				mt: 3,
				mb: 2,
				pb: 1,
				fontSize: 11,
				fontWeight: 700,
				letterSpacing: "0.08em",
				textTransform: "uppercase",
				color: "text.secondary",
				borderBottom: 1,
				borderColor: "divider",
			}}
		>
			{children}
		</Typography>
	);
}

/**
 * SIMS-8936 (AC3): one hex predicate shared by the field that REPORTS an invalid colour and the
 * form rule that BLOCKS the save on one, so the message and the gate can never disagree.
 * An empty value is not "invalid" here — it means the field was left unset.
 */
const isBlankOrHex = (value: string) =>
	value.length === 0 || /^#[0-9A-Fa-f]{6}$/.test(value);

/**
 * Seeds the registered form value once, on mount.
 *
 * A `<Controller>` render prop is a plain callback, NOT a component, so a hook called
 * directly inside it breaks the Rules of Hooks (React identifies hooks by call order
 * within a component; a render prop has no stable identity of its own). Holding the
 * effect in this real component gives it a proper place to live, while the picker's
 * markup stays where it is.
 */
function SeedFormValue({ seed }: { seed: () => void }) {
	// Mount-only by design: this writes the picker's initial JSON into the form so the
	// field is populated before the first submit. `seed` is deliberately excluded from the
	// deps — re-running it on every render would overwrite edits in progress. Held in a ref
	// so the effect never needs it as a dependency.
	const seedRef = useRef(seed);
	seedRef.current = seed;
	useEffect(() => {
		seedRef.current();
	}, []);
	return null;
}

function ColorField({
	label,
	value,
	placeholder,
	onChange,
	disabled = false,
	helperText,
}: {
	label: string;
	value: string;
	placeholder: string;
	onChange: (hex: string) => void;
	disabled?: boolean;
	helperText?: string;
}) {
	// SIMS-8936 (AC3): the call sites already REJECT an invalid hex — they skip buildJson, so the
	// value never persists — but nothing told the admin, so a typo looked like a silent no-op.
	// Surface it the way the rest of this modal surfaces validation (error + helperText on the
	// MUI TextField, exactly as the `module`/`section`/`default` fields do); `error` also sets
	// aria-invalid for us. An empty value is NOT an error: it is how a field is left unset.
	const invalid = !isBlankOrHex(value);
	return (
		// SIMS-8376 AC4: when disabled (Ornamental selected) the whole field dims (opacity .55),
		// per the Hi-Fi handoff, and the helper text explains the border-color scope (FR-2).
		<Box sx={{ opacity: disabled ? 0.55 : 1 }}>
			<Box display="flex" alignItems="center" gap={2}>
				<input
					type="color"
					// A native colour input rejects anything that is not a 6-digit hex and would warn
					// then fall back to #000000, which reads as "your typo was accepted as black".
					// Show a neutral swatch while the text is invalid (same guard as
					// ColorFieldWithOpacity), and let the TextField carry the error.
					value={invalid ? "#FFFFFF" : value}
					disabled={disabled}
					onChange={(e) => onChange(e.target.value)}
					style={{
						width: 48,
						height: 48,
						border: "1px solid #ccc",
						borderRadius: 8,
						cursor: disabled ? "not-allowed" : "pointer",
						padding: 2,
					}}
				/>
				<TextField
					label={label}
					value={value}
					disabled={disabled}
					onChange={(e) => {
						const val = e.target.value;
						onChange(val);
					}}
					size="small"
					fullWidth
					placeholder={placeholder}
					error={invalid}
					helperText={
						invalid ? "Enter a 6-digit hex colour, e.g. #1A1A1A" : undefined
					}
				/>
			</Box>
			{!invalid && helperText && (
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						gap: 0.5,
						mt: 0.5,
					}}
				>
					<InfoOutlinedIcon sx={{ fontSize: 16, mt: "1px", flex: "none" }} />
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ lineHeight: 1.4 }}
					>
						{helperText}
					</Typography>
				</Box>
			)}
		</Box>
	);
}

function getSwatchTextColor(hex: string): string {
	try {
		return parseInt(hex.replace("#", ""), 16) > 0x7fffff ? "#000" : "#FFF";
	} catch {
		return "#FFF";
	}
}

/**
 * Color picker sub-component for the themeColors setting.
 * Shows individual color pickers for primary, secondary, surface + font family.
 *
 * Based on B2D brand spec:
 *  - Primary (#276048) -> navigation, menu, buttons
 *  - Secondary (#70A815) -> secondary actions, badges
 *  - Surface (#DEE7E7) -> backgrounds, cards
 *  - Font: Roboto (Regular, Medium, Semibold, Bold)
 */

interface ThemeEmitState {
	primary: string;
	secondary: string;
	tertiary: string;
	surfaceTint: string;
	// SIMS-8458 AC7/AC8
	successLight: string;
	successDark: string;
	brandFaint: string;
	dividerColor: string;
	surfaceTwo: string;
	textPrimaryMain: string;
	surface: string;
	surfLight: string;
	surfLightOp: number;
	surfDark: string;
	surfDarkOp: number;
	primDark: string;
	primDarkOp: number;
	secLight: string;
	secLightOp: number;
	outMain: string;
	outMainOp: number;
	outDark: string;
	outDarkOp: number;
	txtMainDk2: string;
	txtMainDk2Op: number;
	txtMainMn2: string;
	txtMainMn2Op: number;
	txtSecMain: string;
	txtSecMainOp: number;
	font: string;
}

function ThemeColorPicker({
	control,
	watch,
}: {
	control: ReturnType<typeof useForm<FormData>>["control"];
	watch: ReturnType<typeof useForm<FormData>>["watch"];
}) {
	const currentValue = watch("default");

	// Base colors
	const [primaryColor, setPrimaryColor] = useState("#52197B");
	const [secondaryColor, setSecondaryColor] = useState("#4CB7A4");
	// Tertiary brand accent (JANUS lilac); the theme pipeline falls back to this
	// same default (#7034E7) when the setting omits tertiaryColor.
	const [tertiaryColor, setTertiaryColor] = useState("#7034E7");
	// Tinted-surface role (JANUS blue-100); the theme pipeline falls back to this
	// default (#DFF5FF) when the setting omits surfaceTint.
	const [surfaceTint, setSurfaceTint] = useState("#DFF5FF");
	// SIMS-8458 AC7 — the design-system roles this story adds, each seeded with its
	// documented default so an account that has never overridden one shows the JANUS value.
	const [successLight, setSuccessLight] = useState(DEFAULT_SUCCESS_LIGHT);
	const [successDark, setSuccessDark] = useState(DEFAULT_SUCCESS_DARK);
	const [brandFaint, setBrandFaint] = useState(DEFAULT_BRAND_FAINT);
	const [dividerColor, setDividerColor] = useState(DEFAULT_DIVIDER);
	const [surfaceTwo, setSurfaceTwo] = useState(DEFAULT_SURFACE_TWO);
	const [textPrimaryMain, setTextPrimaryMain] = useState(DEFAULT_TEXT_PRIMARY);
	const [surfaceColor, setSurfaceColor] = useState("#F7F4F9");

	// Surface light/dark with opacity
	const [surfaceLight, setSurfaceLight] = useState("#FFFFFF");
	const [surfaceLightOpacity, setSurfaceLightOpacity] = useState(100);
	const [surfaceDark, setSurfaceDark] = useState("#E9D0F4");
	const [surfaceDarkOpacity, setSurfaceDarkOpacity] = useState(100);

	// Advanced overrides (empty string = not set, use derived value)
	const [primDark, setPrimDark] = useState("");
	const [primDarkOp, setPrimDarkOp] = useState(100);
	const [secLight, setSecLight] = useState("");
	const [secLightOp, setSecLightOp] = useState(100);
	const [outMain, setOutMain] = useState("");
	const [outMainOp, setOutMainOp] = useState(100);
	const [outDark, setOutDark] = useState("");
	const [outDarkOp, setOutDarkOp] = useState(100);
	const [txtMainDk2, setTxtMainDk2] = useState("");
	const [txtMainDk2Op, setTxtMainDk2Op] = useState(100);
	const [txtMainMn2, setTxtMainMn2] = useState("");
	const [txtMainMn2Op, setTxtMainMn2Op] = useState(100);
	const [txtSecMain, setTxtSecMain] = useState("");
	const [txtSecMainOp, setTxtSecMainOp] = useState(100);

	const [fontFamily, setFontFamily] = useState("Roboto");

	// Parse current JSON value into individual fields
	useEffect(() => {
		try {
			const parsed =
				typeof currentValue === "string"
					? JSON.parse(currentValue)
					: currentValue;
			if (parsed?.primaryColor) setPrimaryColor(parsed.primaryColor);
			if (parsed?.secondaryColor) setSecondaryColor(parsed.secondaryColor);
			if (parsed?.tertiaryColor) setTertiaryColor(parsed.tertiaryColor);
			if (parsed?.surfaceTint) setSurfaceTint(parsed.surfaceTint);
			// SIMS-8458 AC8 — a saved override populates the field; an absent key leaves the
			// documented default in place, which is what keeps defaults implicit.
			if (parsed?.successLight) setSuccessLight(parsed.successLight);
			if (parsed?.successDark) setSuccessDark(parsed.successDark);
			if (parsed?.brandFaint) setBrandFaint(parsed.brandFaint);
			if (parsed?.dividerColor) setDividerColor(parsed.dividerColor);
			if (parsed?.surfaceTwo) setSurfaceTwo(parsed.surfaceTwo);
			if (parsed?.textPrimaryMain) setTextPrimaryMain(parsed.textPrimaryMain);
			if (parsed?.surfaceColor) setSurfaceColor(parsed.surfaceColor);
			if (parsed?.surfaceLight) {
				const { hex, opacityPct } = parseColorValue(parsed.surfaceLight);
				setSurfaceLight(hex);
				setSurfaceLightOpacity(opacityPct);
			}
			if (parsed?.surfaceDark) {
				const { hex, opacityPct } = parseColorValue(parsed.surfaceDark);
				setSurfaceDark(hex);
				setSurfaceDarkOpacity(opacityPct);
			}
			if (parsed?.primaryDark) {
				const { hex, opacityPct } = parseColorValue(parsed.primaryDark);
				setPrimDark(hex);
				setPrimDarkOp(opacityPct);
			}
			if (parsed?.secondaryLight) {
				const { hex, opacityPct } = parseColorValue(parsed.secondaryLight);
				setSecLight(hex);
				setSecLightOp(opacityPct);
			}
			if (parsed?.outlineMain) {
				const { hex, opacityPct } = parseColorValue(parsed.outlineMain);
				setOutMain(hex);
				setOutMainOp(opacityPct);
			}
			if (parsed?.outlineDark) {
				const { hex, opacityPct } = parseColorValue(parsed.outlineDark);
				setOutDark(hex);
				setOutDarkOp(opacityPct);
			}
			if (parsed?.textMainDark2) {
				const { hex, opacityPct } = parseColorValue(parsed.textMainDark2);
				setTxtMainDk2(hex);
				setTxtMainDk2Op(opacityPct);
			}
			if (parsed?.textMainMain2) {
				const { hex, opacityPct } = parseColorValue(parsed.textMainMain2);
				setTxtMainMn2(hex);
				setTxtMainMn2Op(opacityPct);
			}
			if (parsed?.textSecondaryMain) {
				const { hex, opacityPct } = parseColorValue(parsed.textSecondaryMain);
				setTxtSecMain(hex);
				setTxtSecMainOp(opacityPct);
			}
			if (parsed?.fontFamily) setFontFamily(parsed.fontFamily);
		} catch {
			// Keep current state if parse fails
		}
	}, []);

	const buildJson = useCallback(
		(state: ThemeEmitState, onChange: (value: string) => void) => {
			const obj: Record<string, string> = {
				primaryColor: state.primary,
				secondaryColor: state.secondary,
			};
			// Emit tertiary only when it overrides the documented default (#7034E7);
			// the theme pipeline supplies that default when tertiaryColor is absent.
			if (state.tertiary && state.tertiary !== "#7034E7")
				obj.tertiaryColor = state.tertiary;
			// Emit surfaceTint only when overriding the documented default (#DFF5FF);
			// the theme pipeline supplies that default when surfaceTint is absent.
			if (state.surfaceTint && state.surfaceTint !== "#DFF5FF")
				obj.surfaceTint = state.surfaceTint;
			// SIMS-8458 AC8 — emit each role ONLY when it overrides its documented default.
			// Keeping defaults implicit is the point: a tenant that never touched a field
			// carries no key for it, so a later change to the default in colorUtils.ts still
			// reaches them. Same contract as tertiaryColor / surfaceTint above.
			if (state.successLight && state.successLight !== DEFAULT_SUCCESS_LIGHT)
				obj.successLight = state.successLight;
			if (state.successDark && state.successDark !== DEFAULT_SUCCESS_DARK)
				obj.successDark = state.successDark;
			if (state.brandFaint && state.brandFaint !== DEFAULT_BRAND_FAINT)
				obj.brandFaint = state.brandFaint;
			if (state.dividerColor && state.dividerColor !== DEFAULT_DIVIDER)
				obj.dividerColor = state.dividerColor;
			if (state.surfaceTwo && state.surfaceTwo !== DEFAULT_SURFACE_TWO)
				obj.surfaceTwo = state.surfaceTwo;
			if (
				state.textPrimaryMain &&
				state.textPrimaryMain !== DEFAULT_TEXT_PRIMARY
			)
				obj.textPrimaryMain = state.textPrimaryMain;
			if (state.surface && state.surface !== "#F7F4F9")
				obj.surfaceColor = state.surface;
			const surfLightVal = buildColorValue(state.surfLight, state.surfLightOp);
			if (surfLightVal !== "#FFFFFF") obj.surfaceLight = surfLightVal;
			const surfDarkVal = buildColorValue(state.surfDark, state.surfDarkOp);
			if (surfDarkVal !== "#E9D0F4") obj.surfaceDark = surfDarkVal;
			if (state.primDark)
				obj.primaryDark = buildColorValue(state.primDark, state.primDarkOp);
			if (state.secLight)
				obj.secondaryLight = buildColorValue(state.secLight, state.secLightOp);
			if (state.outMain)
				obj.outlineMain = buildColorValue(state.outMain, state.outMainOp);
			if (state.outDark)
				obj.outlineDark = buildColorValue(state.outDark, state.outDarkOp);
			if (state.txtMainDk2)
				obj.textMainDark2 = buildColorValue(
					state.txtMainDk2,
					state.txtMainDk2Op,
				);
			if (state.txtMainMn2)
				obj.textMainMain2 = buildColorValue(
					state.txtMainMn2,
					state.txtMainMn2Op,
				);
			if (state.txtSecMain)
				obj.textSecondaryMain = buildColorValue(
					state.txtSecMain,
					state.txtSecMainOp,
				);
			if (state.font && state.font !== "Roboto") obj.fontFamily = state.font;
			onChange(JSON.stringify(obj));
		},
		[],
	);

	// The Controller's onChange only exists inside its render prop, but the seeding effect
	// below must be a TOP-LEVEL hook (Rules of Hooks — a hook inside a render callback is a
	// conditional/nested call). Bridge the two with a ref, assigned during render.
	const onChangeRef = useRef<((value: string) => void) | null>(null);

	// Seed the form value ONLY when nothing is saved yet, i.e. when creating a new setting.
	// The empty dependency array is what makes this "on mount", and it is deliberate.
	//
	// This used to live inside the Controller's render prop AND run unconditionally, which
	// caused real data loss: on EDIT it fired before the parse effect above had loaded the
	// saved JSON into state, writing the initial useState defaults over the stored value.
	// The screen still showed the tenant's colours (the parse effect wins the race
	// visually) while the form held defaults — so opening an account to look at it was
	// enough to replace its theme on the next save.
	//
	// `currentValue` is intentionally not in the deps: this is a one-shot seed for the
	// create case, and re-running it on every keystroke would fight the user's edits.
	useEffect(() => {
		const hasSavedValue =
			typeof currentValue === "string"
				? currentValue.trim().length > 0
				: currentValue != null;
		if (hasSavedValue) return;
		const onChange = onChangeRef.current;
		if (!onChange) return;
		buildJson(
			{
				primary: primaryColor,
				secondary: secondaryColor,
				tertiary: tertiaryColor,
				surfaceTint,
				successLight,
				successDark,
				brandFaint,
				dividerColor,
				surfaceTwo,
				textPrimaryMain,
				surface: surfaceColor,
				surfLight: surfaceLight,
				surfLightOp: surfaceLightOpacity,
				surfDark: surfaceDark,
				surfDarkOp: surfaceDarkOpacity,
				primDark,
				primDarkOp,
				secLight,
				secLightOp,
				outMain,
				outMainOp,
				outDark,
				outDarkOp,
				txtMainDk2,
				txtMainDk2Op,
				txtMainMn2,
				txtMainMn2Op,
				txtSecMain,
				txtSecMainOp,
				font: fontFamily,
			},
			onChange,
		);
	}, []);

	return (
		<Controller
			name="default"
			control={control}
			rules={{ required: "Theme colors are required" }}
			render={({ field: { onChange } }) => {
				// Hand the Controller's onChange to the seeding effect, which lives at the
				// component's top level (see seedOnce above). Assigning to a ref during
				// render is safe — it is not state, so it triggers no re-render — and it is
				// the standard way to give a top-level hook access to a value that only
				// exists inside a render prop.
				onChangeRef.current = onChange;

				const emit = (overrides: Partial<ThemeEmitState>) => {
					buildJson(
						{
							primary: primaryColor,
							secondary: secondaryColor,
							tertiary: tertiaryColor,
							surfaceTint,
							successLight,
							successDark,
							brandFaint,
							dividerColor,
							surfaceTwo,
							textPrimaryMain,
							surface: surfaceColor,
							surfLight: surfaceLight,
							surfLightOp: surfaceLightOpacity,
							surfDark: surfaceDark,
							surfDarkOp: surfaceDarkOpacity,
							primDark,
							primDarkOp,
							secLight,
							secLightOp,
							outMain,
							outMainOp,
							outDark,
							outDarkOp,
							txtMainDk2,
							txtMainDk2Op,
							txtMainMn2,
							txtMainMn2Op,
							txtSecMain,
							txtSecMainOp,
							font: fontFamily,
							...overrides,
						},
						onChange,
					);
				};

				return (
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 2 }}>
							Theme Colors & Font
						</Typography>
						<Grid container spacing={2}>
							{/* Primary Color */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Primary Color"
									value={primaryColor}
									placeholder="#276048"
									onChange={(val) => {
										setPrimaryColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ primary: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Buttons, links, active states
								</Typography>
							</Grid>
							{/* Secondary Color */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Secondary Color"
									value={secondaryColor}
									placeholder="#70A815"
									onChange={(val) => {
										setSecondaryColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ secondary: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Secondary actions, badges, links
								</Typography>
							</Grid>
							{/* Tertiary Color */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Tertiary Color"
									value={tertiaryColor}
									placeholder="#7034E7"
									onChange={(val) => {
										setTertiaryColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ tertiary: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Accent: active tab, links, chart line
								</Typography>
							</Grid>
							{/* Surface Tint */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Surface Tint"
									value={surfaceTint}
									placeholder="#DFF5FF"
									onChange={(val) => {
										setSurfaceTint(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ surfaceTint: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Tinted surfaces: table headers, tint panels
								</Typography>
							</Grid>
							{/* SIMS-8458 AC7 — design-system roles added by this story.
							    Text Secondary is deliberately NOT repeated here: the screen
							    already exposes it (with opacity) further down, and that same
							    field is one of the roles AC4 repoints, so a second control
							    would be two similarly-named fields with different effects. */}
							{/* Success Light */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Success Light"
									value={successLight}
									placeholder={DEFAULT_SUCCESS_LIGHT}
									onChange={(val) => {
										setSuccessLight(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ successLight: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Active pill background, completed journey steps
								</Typography>
							</Grid>
							{/* Success Dark */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Success Dark"
									value={successDark}
									placeholder={DEFAULT_SUCCESS_DARK}
									onChange={(val) => {
										setSuccessDark(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ successDark: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Active pill text, earned incentive amounts
								</Typography>
							</Grid>
							{/* Brand Faint */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Brand Faint"
									value={brandFaint}
									placeholder={DEFAULT_BRAND_FAINT}
									onChange={(val) => {
										setBrandFaint(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ brandFaint: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Selected day card in Daily Journey
								</Typography>
							</Grid>
							{/* Divider */}
							<Grid item xs={12} sm={6}>
								<ColorField
									// "Table Divider" rather than the design's bare token name
									// `--divider`: the screen already has Outline Main / Outline
									// Dark, which also draw borders and separators, so a field
									// labelled just "Divider" gave an admin no way to tell the three
									// apart. The token name lives in the code; the label is for
									// whoever is picking colours.
									label="Table Divider"
									value={dividerColor}
									placeholder={DEFAULT_DIVIDER}
									onChange={(val) => {
										setDividerColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ dividerColor: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Framed-table borders and in-card separators
								</Typography>
							</Grid>
							{/* Neutral Surface — SIMS-8458 AC11 */}
							<Grid item xs={12} sm={6}>
								<ColorField
									// Shares a default with Table Divider on purpose: the design
									// defines `--divider: var(--surface-2)`. Kept as a separate
									// field because they are separate concepts — a tenant could
									// retint one without the other.
									label="Neutral Surface"
									value={surfaceTwo}
									placeholder={DEFAULT_SURFACE_TWO}
									onChange={(val) => {
										setSurfaceTwo(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ surfaceTwo: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Not-started journey steps, neutral chips
								</Typography>
							</Grid>
							{/* Text Primary */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Text Primary"
									value={textPrimaryMain}
									placeholder={DEFAULT_TEXT_PRIMARY}
									onChange={(val) => {
										setTextPrimaryMain(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ textPrimaryMain: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Body text app-wide (headings, table cells)
								</Typography>
							</Grid>
							{/* Surface Color */}
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Surface Color"
									value={surfaceColor}
									placeholder="#DEE7E7"
									onChange={(val) => {
										setSurfaceColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ surface: val });
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Page backgrounds, card backgrounds
								</Typography>
							</Grid>
							{/* Surface Light */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Surface Light"
									hexValue={surfaceLight}
									opacityPct={surfaceLightOpacity}
									placeholder="#FFFFFF"
									caption="Dialog headers, buttons text, menu items"
									onHexChange={(val) => {
										setSurfaceLight(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ surfLight: val });
									}}
									onOpacityChange={(op) => {
										setSurfaceLightOpacity(op);
										emit({ surfLightOp: op });
									}}
								/>
							</Grid>
							{/* Surface Dark */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Surface Dark"
									hexValue={surfaceDark}
									opacityPct={surfaceDarkOpacity}
									placeholder="#E9D0F4"
									caption="Dividers, hover overlays, tinted panels"
									onHexChange={(val) => {
										setSurfaceDark(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ surfDark: val });
									}}
									onOpacityChange={(op) => {
										setSurfaceDarkOpacity(op);
										emit({ surfDarkOp: op });
									}}
								/>
							</Grid>
							{/* Primary Dark */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Primary Dark"
									hexValue={primDark}
									opacityPct={primDarkOp}
									placeholder="#52197B (auto)"
									caption="Dialog title bars, headers"
									onHexChange={(val) => {
										setPrimDark(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ primDark: val });
									}}
									onOpacityChange={(op) => {
										setPrimDarkOp(op);
										if (primDark) emit({ primDarkOp: op });
									}}
								/>
							</Grid>
							{/* Secondary Light */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Secondary Light"
									hexValue={secLight}
									opacityPct={secLightOp}
									placeholder="#E7EFB8 (auto)"
									caption="Status chips, report labels"
									onHexChange={(val) => {
										setSecLight(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ secLight: val });
									}}
									onOpacityChange={(op) => {
										setSecLightOp(op);
										if (secLight) emit({ secLightOp: op });
									}}
								/>
							</Grid>
							{/* Outline Main */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Outline Main"
									hexValue={outMain}
									opacityPct={outMainOp}
									placeholder="#D9D1DD (auto)"
									caption="Input borders, dialog outlines"
									onHexChange={(val) => {
										setOutMain(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ outMain: val });
									}}
									onOpacityChange={(op) => {
										setOutMainOp(op);
										if (outMain) emit({ outMainOp: op });
									}}
								/>
							</Grid>
							{/* Outline Dark */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Outline Dark"
									hexValue={outDark}
									opacityPct={outDarkOp}
									placeholder="#ADA6B3 (auto)"
									// Not just borders: general.ts:30/34 reads outline.dark for the
									// Excused-absence and Missed-visit clinical statuses, so changing
									// this recolours those states too. Worth saying out loud here —
									// AC5 protects it in code, but nothing warned the admin.
									caption="Menu borders, inactive chips — also Excused absence / Missed visit"
									onHexChange={(val) => {
										setOutDark(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) emit({ outDark: val });
									}}
									onOpacityChange={(op) => {
										setOutDarkOp(op);
										if (outDark) emit({ outDarkOp: op });
									}}
								/>
							</Grid>
							{/* Text Main Dark2 */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Text Main Dark"
									hexValue={txtMainDk2}
									opacityPct={txtMainDk2Op}
									placeholder="#1F2026 (auto)"
									caption="Body text, labels, card content"
									onHexChange={(val) => {
										setTxtMainDk2(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ txtMainDk2: val });
									}}
									onOpacityChange={(op) => {
										setTxtMainDk2Op(op);
										if (txtMainDk2) emit({ txtMainDk2Op: op });
									}}
								/>
							</Grid>
							{/* Text Main Main2 */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Text Main Secondary"
									hexValue={txtMainMn2}
									opacityPct={txtMainMn2Op}
									placeholder="#79797D (auto)"
									caption="Secondary text, date cards, list items"
									onHexChange={(val) => {
										setTxtMainMn2(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ txtMainMn2: val });
									}}
									onOpacityChange={(op) => {
										setTxtMainMn2Op(op);
										if (txtMainMn2) emit({ txtMainMn2Op: op });
									}}
								/>
							</Grid>
							{/* Text Secondary Main */}
							<Grid item xs={12} sm={6}>
								<ColorFieldWithOpacity
									label="Text Secondary"
									hexValue={txtSecMain}
									opacityPct={txtSecMainOp}
									placeholder="#79797D (auto)"
									caption="Muted labels, form hints"
									onHexChange={(val) => {
										setTxtSecMain(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val))
											emit({ txtSecMain: val });
									}}
									onOpacityChange={(op) => {
										setTxtSecMainOp(op);
										if (txtSecMain) emit({ txtSecMainOp: op });
									}}
								/>
							</Grid>
							{/* Font Family */}
							<Grid item xs={12} sm={6}>
								<TextField
									label="Font Family"
									value={fontFamily}
									onChange={(e) => {
										const val = e.target.value;
										setFontFamily(val);
										emit({ font: val });
									}}
									size="small"
									fullWidth
									placeholder="Roboto"
									helperText="Regular, Medium, Semibold, Bold weights"
								/>
							</Grid>
						</Grid>

						{/* Preview swatches */}
						<Typography
							variant="caption"
							sx={{ mt: 2, mb: 1, display: "block" }}
						>
							Preview
						</Typography>
						<Box display="flex" gap={1.5} flexWrap="wrap">
							{[
								{ color: primaryColor, label: "Primary" },
								{ color: secondaryColor, label: "Secondary" },
								{ color: surfaceColor, label: "Surface" },
								{
									color: buildColorValue(surfaceLight, surfaceLightOpacity),
									label: "Surf. Light",
								},
								{
									color: buildColorValue(surfaceDark, surfaceDarkOpacity),
									label: "Surf. Dark",
								},
								...(primDark
									? [
											{
												color: buildColorValue(primDark, primDarkOp),
												label: "Prim. Dark",
											},
										]
									: []),
								...(secLight
									? [
											{
												color: buildColorValue(secLight, secLightOp),
												label: "Sec. Light",
											},
										]
									: []),
								...(outMain
									? [
											{
												color: buildColorValue(outMain, outMainOp),
												label: "Outline",
											},
										]
									: []),
								...(txtMainDk2
									? [
											{
												color: buildColorValue(txtMainDk2, txtMainDk2Op),
												label: "Txt Dark",
											},
										]
									: []),
							].map(({ color, label }) => (
								<Box
									key={label}
									sx={{
										width: 90,
										height: 44,
										backgroundColor: color,
										borderRadius: 1,
										border: "1px solid #ccc",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<Typography
										variant="caption"
										sx={{
											color: getSwatchTextColor(
												color.startsWith("rgba") ? "#888888" : color,
											),
											fontFamily: fontFamily || "Roboto",
											fontWeight: 600,
										}}
									>
										{label}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>
				);
			}}
		/>
	);
}

/**
 * Editor sub-component for the beneficiaryTerms setting.
 * Shows two text fields for singular and plural terms.
 */
function BeneficiaryTermsPicker({
	control,
	watch,
}: {
	control: ReturnType<typeof useForm<FormData>>["control"];
	watch: ReturnType<typeof useForm<FormData>>["watch"];
}) {
	const currentValue = watch("default");
	const [singularTerm, setSingularTerm] = useState("Beneficiary");
	const [pluralTerm, setPluralTerm] = useState("Beneficiaries");

	useEffect(() => {
		try {
			const parsed =
				typeof currentValue === "string"
					? JSON.parse(currentValue)
					: currentValue;
			if (parsed?.singular) setSingularTerm(parsed.singular);
			if (parsed?.plural) setPluralTerm(parsed.plural);
		} catch {
			// Keep current state if parse fails
		}
	}, []);

	const buildJson = useCallback(
		(singular: string, plural: string, onChange: (value: string) => void) => {
			onChange(JSON.stringify({ singular, plural }));
		},
		[],
	);

	return (
		<Controller
			name="default"
			control={control}
			rules={{ required: "Beneficiary terms are required" }}
			render={({ field: { onChange } }) => {
				// Sync form value on mount so validation passes
				useEffect(() => {
					buildJson(singularTerm, pluralTerm, onChange);
				}, []);

				return (
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 2 }}>
							Beneficiary Terminology
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Singular Term"
									value={singularTerm}
									onChange={(e) => {
										const val = e.target.value;
										setSingularTerm(val);
										buildJson(val, pluralTerm, onChange);
									}}
									fullWidth
									placeholder="Beneficiary"
									helperText='e.g., "Participant", "Client", "Member"'
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Plural Term"
									value={pluralTerm}
									onChange={(e) => {
										const val = e.target.value;
										setPluralTerm(val);
										buildJson(singularTerm, val, onChange);
									}}
									fullWidth
									placeholder="Beneficiaries"
									helperText='e.g., "Participants", "Clients", "Members"'
								/>
							</Grid>
						</Grid>
					</Box>
				);
			}}
		/>
	);
}

/**
 * Editor sub-component for the certificateConfig setting.
 * Shows fields for logos, colors, text, toggles matching the certificate template.
 */
// Exported for unit testing (SIMS-8376 AC4 — border-color scope communication). The component is
// otherwise only used inside this module by the settings form.
export function CertificateConfigPicker({
	control,
	watch,
	error,
}: {
	control: ReturnType<typeof useForm<FormData>>["control"];
	watch: ReturnType<typeof useForm<FormData>>["watch"];
	// SIMS-8936 (AC3): this picker REPLACES the `default` TextField, so the form-level message for
	// `errors.default` has nowhere to render — without this the save would block with no
	// explanation. The parent passes it down and we show it at the top of the editor.
	error?: string;
}) {
	const currentValue = watch("default");

	// FR-12: all defaults come from the ONE shared contract, so the editor and the
	// renderer can never drift apart (previously these literals diverged, e.g.
	// titleColor #1a1a1a here vs #C67801 in the renderer).
	const D = CERTIFICATE_CONFIG_DEFAULTS;
	const [accountLogoUrl, setAccountLogoUrl] = useState(D.accountLogo.url);
	const [accountLogoWidth, setAccountLogoWidth] = useState(D.accountLogo.width);
	const [accountLogoHeight, setAccountLogoHeight] = useState(
		D.accountLogo.height,
	);
	const [secondaryLogoUrl, setSecondaryLogoUrl] = useState(D.secondaryLogo.url);
	const [secondaryLogoWidth, setSecondaryLogoWidth] = useState(
		D.secondaryLogo.width,
	);
	const [secondaryLogoHeight, setSecondaryLogoHeight] = useState(
		D.secondaryLogo.height,
	);
	const [titleText, setTitleText] = useState(D.titleText);
	const [titleColor, setTitleColor] = useState(D.titleColor);
	const [titleFontSize, setTitleFontSize] = useState(D.titleFontSize);
	const [primaryTextColor, setPrimaryTextColor] = useState(D.primaryTextColor);
	const [secondaryTextColor, setSecondaryTextColor] = useState(
		D.secondaryTextColor,
	);
	const [congratulationsText, setCongratulationsText] = useState(
		D.congratulationsText,
	);
	const [completionMessageTemplate, setCompletionMessageTemplate] = useState(
		D.completionMessageTemplate,
	);
	const [coordinatorLabel, setCoordinatorLabel] = useState(
		D.footerLabels.coordinator,
	);
	const [dateLabel, setDateLabel] = useState(D.footerLabels.date);
	const [showCoordinatorName, setShowCoordinatorName] = useState(
		D.showCoordinatorName,
	);
	// SIMS-8424 (FR-15): custom text replacing the enrollment's program name on the certificate label.
	// SIMS-8416 (FR-8): the certificate title's font.
	const [titleFont, setTitleFont] = useState(D.titleFont);
	const [programLabelOverride, setProgramLabelOverride] = useState(
		D.programLabelOverride,
	);
	const [coordinatorTitleOverride, setCoordinatorTitleOverride] = useState(
		D.coordinatorTitleOverride,
	);
	const [showTrophyIcon, setShowTrophyIcon] = useState(D.showTrophyIcon);
	// SIMS-8400 / SIMS-8408 (FR-7): each icon carries its own colour.
	const [trophyColor, setTrophyColor] = useState(D.trophyColor);
	const [showBadgesIcon, setShowBadgesIcon] = useState(D.showBadgesIcon);
	const [badgeColor, setBadgeColor] = useState(D.badgeColor);
	const [borderStyle, setBorderStyle] = useState(D.borderStyle);
	const [borderColor, setBorderColor] = useState(D.borderColor);
	const [backgroundColor, setBackgroundColor] = useState(D.backgroundColor);
	const [backgroundImage, setBackgroundImage] = useState(D.backgroundImage);
	// SIMS-8392 (FR-4 / design §5): warn the admin when a configured background image cannot be embedded
	// (host blocks cross-origin, or it is unreachable) — the certificate then falls back to the background
	// color, so the on-screen preview would not match the PDF. Checked via the same resolver the renderer
	// uses; "" means it could not be embedded.
	const [bgImageEmbeddable, setBgImageEmbeddable] = useState(true);

	useEffect(() => {
		try {
			const parsed =
				typeof currentValue === "string"
					? JSON.parse(currentValue)
					: currentValue;
			if (!parsed) return;
			if (parsed.accountLogo?.url) setAccountLogoUrl(parsed.accountLogo.url);
			if (parsed.accountLogo?.width)
				setAccountLogoWidth(parsed.accountLogo.width);
			if (parsed.accountLogo?.height)
				setAccountLogoHeight(parsed.accountLogo.height);
			if (parsed.secondaryLogo?.url)
				setSecondaryLogoUrl(parsed.secondaryLogo.url);
			if (parsed.secondaryLogo?.width)
				setSecondaryLogoWidth(parsed.secondaryLogo.width);
			if (parsed.secondaryLogo?.height)
				setSecondaryLogoHeight(parsed.secondaryLogo.height);
			if (parsed.titleText) setTitleText(parsed.titleText);
			if (parsed.titleColor) setTitleColor(parsed.titleColor);
			if (parsed.titleFontSize) setTitleFontSize(parsed.titleFontSize);
			if (parsed.primaryTextColor) setPrimaryTextColor(parsed.primaryTextColor);
			if (parsed.secondaryTextColor)
				setSecondaryTextColor(parsed.secondaryTextColor);
			if (parsed.congratulationsText)
				setCongratulationsText(parsed.congratulationsText);
			if (parsed.completionMessageTemplate)
				setCompletionMessageTemplate(parsed.completionMessageTemplate);
			if (parsed.footerLabels?.coordinator)
				setCoordinatorLabel(parsed.footerLabels.coordinator);
			if (parsed.footerLabels?.date) setDateLabel(parsed.footerLabels.date);
			if (parsed.showCoordinatorName !== undefined)
				setShowCoordinatorName(parsed.showCoordinatorName);
			// !== undefined, not truthy: an empty override is meaningful (it restores the program name),
			// so a cleared value must hydrate as "" rather than being skipped.
			if (parsed.titleFont) setTitleFont(parsed.titleFont);
			if (parsed.programLabelOverride !== undefined)
				setProgramLabelOverride(parsed.programLabelOverride);
			if (parsed.coordinatorTitleOverride)
				setCoordinatorTitleOverride(parsed.coordinatorTitleOverride);
			if (parsed.showTrophyIcon !== undefined)
				setShowTrophyIcon(parsed.showTrophyIcon);
			if (parsed.trophyColor) setTrophyColor(parsed.trophyColor);
			if (parsed.showBadgesIcon !== undefined)
				setShowBadgesIcon(parsed.showBadgesIcon);
			if (parsed.badgeColor) setBadgeColor(parsed.badgeColor);
			if (parsed.borderStyle) setBorderStyle(parsed.borderStyle);
			if (parsed.borderColor) setBorderColor(parsed.borderColor);
			if (parsed.backgroundColor) setBackgroundColor(parsed.backgroundColor);
			if (parsed.backgroundImage !== undefined)
				setBackgroundImage(parsed.backgroundImage);
		} catch {
			// Keep defaults
		}
	}, []);

	// SIMS-8392 (FR-4 / design §5): when a background image URL is set, check whether it can be embedded
	// (CORS). If not, surface the admin warning below the field. Empty URL = nothing to warn about.
	useEffect(() => {
		if (!backgroundImage) {
			setBgImageEmbeddable(true);
			return;
		}
		let active = true;
		resolveEmbeddableImage(backgroundImage).then((dataUri) => {
			if (active) setBgImageEmbeddable(dataUri !== "");
		});
		return () => {
			active = false;
		};
	}, [backgroundImage]);

	const buildJson = useCallback(
		(overrides: Record<string, unknown>, onChange: (value: string) => void) => {
			const obj = {
				accountLogo: {
					url: overrides.accountLogoUrl ?? accountLogoUrl,
					width: overrides.accountLogoWidth ?? accountLogoWidth,
					height: overrides.accountLogoHeight ?? accountLogoHeight,
				},
				secondaryLogo: {
					url: overrides.secondaryLogoUrl ?? secondaryLogoUrl,
					width: overrides.secondaryLogoWidth ?? secondaryLogoWidth,
					height: overrides.secondaryLogoHeight ?? secondaryLogoHeight,
				},
				titleText: overrides.titleText ?? titleText,
				titleColor: overrides.titleColor ?? titleColor,
				titleFontSize: overrides.titleFontSize ?? titleFontSize,
				primaryTextColor: overrides.primaryTextColor ?? primaryTextColor,
				secondaryTextColor: overrides.secondaryTextColor ?? secondaryTextColor,
				congratulationsText:
					overrides.congratulationsText ?? congratulationsText,
				completionMessageTemplate:
					overrides.completionMessageTemplate ?? completionMessageTemplate,
				footerLabels: {
					coordinator: overrides.coordinatorLabel ?? coordinatorLabel,
					date: overrides.dateLabel ?? dateLabel,
				},
				showCoordinatorName:
					overrides.showCoordinatorName ?? showCoordinatorName,
				titleFont: overrides.titleFont ?? titleFont,
				programLabelOverride:
					overrides.programLabelOverride ?? programLabelOverride,
				coordinatorTitleOverride:
					overrides.coordinatorTitleOverride ?? coordinatorTitleOverride,
				showTrophyIcon: overrides.showTrophyIcon ?? showTrophyIcon,
				trophyColor: overrides.trophyColor ?? trophyColor,
				showBadgesIcon: overrides.showBadgesIcon ?? showBadgesIcon,
				badgeColor: overrides.badgeColor ?? badgeColor,
				borderStyle: overrides.borderStyle ?? borderStyle,
				borderColor: overrides.borderColor ?? borderColor,
				backgroundColor: overrides.backgroundColor ?? backgroundColor,
				backgroundImage: overrides.backgroundImage ?? backgroundImage,
			};
			onChange(JSON.stringify(obj));
		},
		[
			accountLogoUrl,
			accountLogoWidth,
			accountLogoHeight,
			secondaryLogoUrl,
			secondaryLogoWidth,
			secondaryLogoHeight,
			titleText,
			titleColor,
			titleFontSize,
			primaryTextColor,
			secondaryTextColor,
			congratulationsText,
			completionMessageTemplate,
			coordinatorLabel,
			dateLabel,
			showCoordinatorName,
			titleFont,
			programLabelOverride,
			coordinatorTitleOverride,
			showTrophyIcon,
			trophyColor,
			showBadgesIcon,
			badgeColor,
			borderStyle,
			borderColor,
			backgroundColor,
			backgroundImage,
		],
	);

	return (
		<Controller
			name="default"
			control={control}
			rules={{
				required: "Certificate config is required",
				// SIMS-8936 (AC3): the whole picker is ONE registered field, so handleSubmit's
				// built-in guard only ever saw a non-empty JSON string and let the save through
				// while a colour field sat visibly in error. The colours are local state, not
				// registered fields, so re-check them here — this is the same `rules`-based gate
				// the rest of the modal relies on, rather than disabling the button (which this
				// form deliberately never does).
				validate: () =>
					[
						titleColor,
						primaryTextColor,
						secondaryTextColor,
						trophyColor,
						badgeColor,
						borderColor,
						backgroundColor,
					].every(isBlankOrHex) ||
					"Fix the highlighted colour fields before saving",
			}}
			render={({ field: { onChange } }) => {
				return (
					<Box>
						{/* Rules of Hooks: the seeding effect lives in a real component (a render
						    prop is a callback, not a component, so a hook here would be invalid). */}
						<SeedFormValue seed={() => buildJson({}, onChange)} />
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						{/* Logos Section */}
						<CertificateSectionHeading>Logos</CertificateSectionHeading>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={8}>
								<TextField
									label="Account Logo URL"
									value={accountLogoUrl}
									onChange={(e) => {
										setAccountLogoUrl(e.target.value);
										buildJson({ accountLogoUrl: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
									placeholder="https://..."
									helperText="PNG or JPEG. Leave empty to show organization name only"
								/>
							</Grid>
							<Grid item xs={6} sm={2}>
								<TextField
									label="Width"
									type="number"
									value={accountLogoWidth}
									onChange={(e) => {
										const val = Number(e.target.value);
										setAccountLogoWidth(val);
										buildJson({ accountLogoWidth: val }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={6} sm={2}>
								<TextField
									label="Height"
									type="number"
									value={accountLogoHeight}
									onChange={(e) => {
										const val = Number(e.target.value);
										setAccountLogoHeight(val);
										buildJson({ accountLogoHeight: val }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12} sm={8}>
								<TextField
									label="Secondary Logo URL (optional)"
									value={secondaryLogoUrl}
									onChange={(e) => {
										setSecondaryLogoUrl(e.target.value);
										buildJson({ secondaryLogoUrl: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
									placeholder="https://..."
									helperText="Optional secondary logo (e.g., partner organization)"
								/>
							</Grid>
							<Grid item xs={6} sm={2}>
								<TextField
									label="Width"
									type="number"
									value={secondaryLogoWidth}
									onChange={(e) => {
										const val = Number(e.target.value);
										setSecondaryLogoWidth(val);
										buildJson({ secondaryLogoWidth: val }, onChange);
									}}
									// SIMS-8440 (FR-11): the design states the bound in helper text; the
									// renderer clamps to 160x80 (a contain box, aspect preserved). No error
									// state or clamp-on-blur — the design draws none.
									helperText="Max 160 px"
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={6} sm={2}>
								<TextField
									label="Height"
									type="number"
									value={secondaryLogoHeight}
									onChange={(e) => {
										const val = Number(e.target.value);
										setSecondaryLogoHeight(val);
										buildJson({ secondaryLogoHeight: val }, onChange);
									}}
									helperText="Max 80 px"
									fullWidth
									size="small"
								/>
							</Grid>
							{(accountLogoUrl || secondaryLogoUrl) && (
								<Grid item xs={12}>
									<Box display="flex" gap={2} alignItems="center">
										{accountLogoUrl && (
											<Box>
												<Typography variant="caption">Account Logo</Typography>
												<img
													src={accountLogoUrl}
													alt="Account logo preview"
													style={{
														maxWidth: accountLogoWidth,
														maxHeight: accountLogoHeight,
														display: "block",
														border: "1px solid #eee",
														borderRadius: 4,
													}}
													onError={(e) => {
														(e.target as HTMLImageElement).style.display =
															"none";
													}}
												/>
											</Box>
										)}
										{secondaryLogoUrl && (
											<Box>
												<Typography variant="caption">
													Secondary Logo
												</Typography>
												<img
													src={secondaryLogoUrl}
													alt="Secondary logo preview"
													style={{
														maxWidth: secondaryLogoWidth,
														maxHeight: secondaryLogoHeight,
														display: "block",
														border: "1px solid #eee",
														borderRadius: 4,
													}}
													onError={(e) => {
														(e.target as HTMLImageElement).style.display =
															"none";
													}}
												/>
											</Box>
										)}
									</Box>
								</Grid>
							)}
						</Grid>

						{/* Title & Text Section */}
						<CertificateSectionHeading>Text</CertificateSectionHeading>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={8}>
								<TextField
									label="Title Text"
									value={titleText}
									onChange={(e) => {
										setTitleText(e.target.value);
										buildJson({ titleText: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<TextField
									label="Title Font Size"
									type="number"
									value={titleFontSize}
									onChange={(e) => {
										const val = Number(e.target.value);
										setTitleFontSize(val);
										buildJson({ titleFontSize: val }, onChange);
									}}
									fullWidth
									size="small"
									inputProps={{ min: 24, max: 72 }}
								/>
							</Grid>
							{/* SIMS-8416 (FR-8): sits in Text directly after Title size, per the
							    hand-off's field order (design ref 1). */}
							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel>Title Font</InputLabel>
									<Select
										value={titleFont}
										label="Title Font"
										onChange={(e) => {
											setTitleFont(e.target.value);
											buildJson({ titleFont: e.target.value }, onChange);
										}}
									>
										{Object.keys(CERTIFICATE_TITLE_FONTS).map((font) => (
											<MenuItem key={font} value={font}>
												{font === CERTIFICATE_CONFIG_DEFAULTS.titleFont
													? `${font} (default)`
													: font}
											</MenuItem>
										))}
										<MenuItem value={CERTIFICATE_TITLE_FONT_MATCH_BODY}>
											Match body text
										</MenuItem>
									</Select>
									<FormHelperText>
										PDF-safe list. &quot;Match body text&quot; uses the
										certificate&apos;s own body font. Title only.
									</FormHelperText>
								</FormControl>
							</Grid>
							{/* SIMS-8424 (FR-15): sits in Text directly after Title size, per the
							    hand-off's field order (design ref 2). */}
							<Grid item xs={12} sm={6}>
								<TextField
									label="Program Name Override"
									value={programLabelOverride}
									onChange={(e) => {
										setProgramLabelOverride(e.target.value);
										buildJson(
											{ programLabelOverride: e.target.value },
											onChange,
										);
									}}
									fullWidth
									size="small"
									helperText="Leave blank to show the enrollment's program name. Supports {programName}."
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									label="Congratulations Text"
									value={congratulationsText}
									onChange={(e) => {
										setCongratulationsText(e.target.value);
										buildJson(
											{ congratulationsText: e.target.value },
											onChange,
										);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									label="Completion Message Template"
									value={completionMessageTemplate}
									onChange={(e) => {
										setCompletionMessageTemplate(e.target.value);
										buildJson(
											{ completionMessageTemplate: e.target.value },
											onChange,
										);
									}}
									fullWidth
									size="small"
									helperText="Variables: {orgName}, {programName}"
								/>
							</Grid>
						</Grid>

						{/* Colors Section */}
						<CertificateSectionHeading>Colors</CertificateSectionHeading>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Title Color"
									value={titleColor}
									placeholder="#1a1a1a"
									onChange={(val) => {
										setTitleColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ titleColor: val }, onChange);
										}
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Primary Text Color"
									value={primaryTextColor}
									placeholder="#333333"
									onChange={(val) => {
										setPrimaryTextColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ primaryTextColor: val }, onChange);
										}
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Body text, beneficiary name
								</Typography>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Secondary Text Color"
									value={secondaryTextColor}
									placeholder="#8B0000"
									onChange={(val) => {
										setSecondaryTextColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ secondaryTextColor: val }, onChange);
										}
									}}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5, mb: 1, lineHeight: 1.4 }}
								>
									Footer labels (Coordinator, Date)
								</Typography>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Border Color"
									value={borderColor}
									placeholder="#DAA520"
									disabled={borderStyle === "gold-ornamental"}
									helperText={
										borderStyle === "gold-ornamental"
											? "Disabled while Ornamental is selected — that frame uses its own artwork colors. Applies to the simple border style."
											: "Applies to the simple border style. The ornamental frame uses its own artwork colors."
									}
									onChange={(val) => {
										setBorderColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ borderColor: val }, onChange);
										}
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<ColorField
									label="Background Color"
									value={backgroundColor}
									placeholder="#FFFFFF"
									onChange={(val) => {
										setBackgroundColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ backgroundColor: val }, onChange);
										}
									}}
								/>
							</Grid>
						</Grid>

						{/* Footer Section */}
						<CertificateSectionHeading>Footer</CertificateSectionHeading>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Coordinator Label"
									value={coordinatorLabel}
									onChange={(e) => {
										setCoordinatorLabel(e.target.value);
										buildJson({ coordinatorLabel: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Date Label"
									value={dateLabel}
									onChange={(e) => {
										setDateLabel(e.target.value);
										buildJson({ dateLabel: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Coordinator Title Override (optional)"
									value={coordinatorTitleOverride}
									onChange={(e) => {
										setCoordinatorTitleOverride(e.target.value);
										buildJson(
											{ coordinatorTitleOverride: e.target.value },
											onChange,
										);
									}}
									fullWidth
									size="small"
									helperText="Override coordinator title text"
								/>
							</Grid>
						</Grid>

						{/* Display Options */}
						<CertificateSectionHeading>
							Display Options
						</CertificateSectionHeading>
						{/* SIMS-8350 — layout direction 1a from the Hi-Fi hand-off (tagged "Selected";
						    1b, the tabbed "Decoration" tab, is "Reference only" and out of scope).
						    "Show coordinator name" keeps its own full-width row; each icon toggle then
						    pairs with its own colour control on the same row. `alignItems: flex-end`
						    matches the design's `align-items:end` — the 40px toggle row bottom-aligns
						    with the 40px colour field that sits under its label. */}
						<Grid container spacing={2} alignItems="flex-end">
							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Switch
											checked={showCoordinatorName}
											onChange={(e) => {
												setShowCoordinatorName(e.target.checked);
												buildJson(
													{ showCoordinatorName: e.target.checked },
													onChange,
												);
											}}
										/>
									}
									label="Show Coordinator Name"
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormControlLabel
									control={
										<Switch
											checked={showTrophyIcon}
											onChange={(e) => {
												setShowTrophyIcon(e.target.checked);
												buildJson(
													{ showTrophyIcon: e.target.checked },
													onChange,
												);
											}}
										/>
									}
									label="Show Trophy Icon"
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								{/* SIMS-8400 (FR-7). Deliberately NOT disabled when the toggle is off: the
								    design never disables it (though it does disable Border Color in the 1b
								    reference), no AC asks for it, and the FR-12 contract requires the value
								    to round-trip losslessly while the icon is hidden. */}
								<ColorField
									label="Trophy Color"
									value={trophyColor}
									placeholder="#C69A2E"
									onChange={(val) => {
										setTrophyColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ trophyColor: val }, onChange);
										}
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormControlLabel
									control={
										<Switch
											checked={showBadgesIcon}
											onChange={(e) => {
												setShowBadgesIcon(e.target.checked);
												buildJson(
													{ showBadgesIcon: e.target.checked },
													onChange,
												);
											}}
										/>
									}
									label="Show Badges Icon"
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								{/* SIMS-8408 (FR-7). Same not-disabled rationale as Trophy Color. */}
								<ColorField
									label="Badge Color"
									value={badgeColor}
									placeholder="#1C8A82"
									onChange={(val) => {
										setBadgeColor(val);
										if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
											buildJson({ badgeColor: val }, onChange);
										}
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel>Border Style</InputLabel>
									<Select
										value={borderStyle}
										label="Border Style"
										onChange={(e) => {
											setBorderStyle(e.target.value);
											buildJson({ borderStyle: e.target.value }, onChange);
										}}
									>
										<MenuItem value="gold-ornamental">Ornamental</MenuItem>
										<MenuItem value="simple">Simple</MenuItem>
										<MenuItem value="none">None</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<TextField
									label="Background Image URL (optional)"
									value={backgroundImage}
									onChange={(e) => {
										setBackgroundImage(e.target.value);
										buildJson({ backgroundImage: e.target.value }, onChange);
									}}
									fullWidth
									size="small"
									placeholder="https://..."
									helperText="Optional custom background image URL"
								/>
								{backgroundImage && !bgImageEmbeddable && (
									<Alert severity="warning" sx={{ mt: 1 }}>
										Background image could not be loaded — the host does not
										allow this image to be reused, so the certificate will use
										the background color instead. Host the image on an approved
										domain to embed it in the PDF.
									</Alert>
								)}
							</Grid>
						</Grid>

						{/* Preview */}
						<Typography
							variant="caption"
							sx={{ mt: 3, mb: 1, display: "block" }}
						>
							Color Preview
						</Typography>
						<Box display="flex" gap={1.5} flexWrap="wrap">
							{[
								{ color: titleColor, label: "Title" },
								{ color: primaryTextColor, label: "Primary" },
								{ color: secondaryTextColor, label: "Secondary" },
								{ color: borderColor, label: "Border" },
								{ color: backgroundColor, label: "Background" },
							].map(({ color, label }) => (
								<Box
									key={label}
									sx={{
										width: 80,
										height: 40,
										backgroundColor: color,
										borderRadius: 1,
										border: "1px solid #ccc",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<Typography
										variant="caption"
										sx={{
											color: getSwatchTextColor(color),
											fontWeight: 600,
											fontSize: "0.65rem",
										}}
									>
										{label}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>
				);
			}}
		/>
	);
}

export default SettingsModal;
